import { useEffect, useState } from "react";
import { Button, Form, Modal } from "semantic-ui-react";
import { FormError } from "../models/FormError";
import { UserFile } from "../models/UserFile";
import FileDropZone from "./common/FileDropZone";
import UserFiles from "./common/UserFiles";
import { useAppContext } from "../hooks/useAppContext";

export interface FormData {
  userFile?: UserFile;
  comments: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onAddPhoto: (comments: string, photo: UserFile) => void;
  userFile?: UserFile;
  onUploadFile: (userFiles: UserFile) => void;
  uploading: boolean;
}

const AddSitePhotoModal = ({ open, onClose, onAddPhoto, userFile, onUploadFile, uploading }: Props) => {
  const { sitePhotoMaxSizeByte } = useAppContext();
  const [formData, setFormData] = useState<FormData>({} as FormData);
  const [formErrors, setFormErrors] = useState<FormError>({});

  useEffect(() => {
    setFormData({} as FormData);
    setFormErrors({} as FormError);
  }, [open]);

  useEffect(() => {
    if (!userFile?.uploadStatus) return;

    if (userFile.uploadStatus.uploaded) {
      setFormData({ ...formData, userFile });
    } else if (userFile.uploadStatus.uploadError) {
      setFormErrors({
        ...formErrors,
        userFile: userFile.uploadStatus?.uploadError.message,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userFile]);

  const validateFormData = (formData: FormData) => {
    const formError: FormError = {};

    if (!formData.userFile || !formData.userFile.uploadStatus.uploaded || formData.userFile.uploadStatus.uploadError) {
      formError.userFile = "Please select a file";
    }

    return formError;
  };

  const handleSubmit = () => {
    const validationErrors = validateFormData(formData);
    setFormErrors(validationErrors);

    // If there is no validation error
    if (Object.keys(validationErrors).length === 0) {
      const { comments, userFile } = formData;
      if (userFile) {
        onAddPhoto(comments, userFile);
      }
    }
  };

  const handelClose = () => {
    onClose();
  };

  const handleAcceptedFilesSelected = (acceptedFiles: UserFile[]) => {
    if (acceptedFiles.length) {
      onUploadFile(acceptedFiles[0]);
      setFormErrors({});
    }
  };

  const handleRejectedFilesSelected = (rejectedFiles: UserFile[]) => {
    if (rejectedFiles.length) {
      setFormErrors({
        ...formErrors,
        userFile: rejectedFiles[0].uploadStatus?.fileErrors
          ? rejectedFiles[0].uploadStatus.fileErrors[0]
          : "Please select a file",
      });
    }
  };

  const handleChange = (e: any, { name, value }: any) => {
    setFormData({ ...formData, [name]: value });
  };

  return (
    <Modal open={open}>
      <Modal.Header>Add photo</Modal.Header>
      <Modal.Content>
        <Modal.Description>
          <Form>
            <Form.Group widths="equal">
              <Form.Field>
                <label>File</label>
                {formErrors.userFile && (
                  <span className="field error">
                    <label>{formErrors.userFile}</label>
                  </span>
                )}
                <FileDropZone
                  maxSizeByte={sitePhotoMaxSizeByte}
                  multiple={false}
                  accept={[".jpg", ".jpeg", ".png"]}
                  onAcceptedFilesSelected={handleAcceptedFilesSelected}
                  onRejectedFilesSelected={handleRejectedFilesSelected}
                />
              </Form.Field>
            </Form.Group>

            {formData.userFile && (
              <Form.Group widths="equal">
                <Form.Field>
                  <UserFiles userFilesMap={new Map([[formData.userFile.id, formData.userFile]])} />
                </Form.Field>
              </Form.Group>
            )}

            <Form.Group widths="equal">
              <Form.TextArea
                rows={2}
                label="Comments"
                error={formErrors.comments}
                name="comments"
                value={formData.comments}
                onChange={handleChange}
              />
            </Form.Group>
          </Form>
        </Modal.Description>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handelClose}>Cancel</Button>
        <Button onClick={handleSubmit} positive loading={uploading} disabled={uploading}>
          Ok
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default AddSitePhotoModal;
